// TODO: feature/templates - review this file
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  createPaymentRequestTemplate,
  updatePaymentRequestTemplate,
} from "@api";
import { toast } from "@components/toast/Toast";
import { Modal, extractFormValues } from "@components";
import { useForm } from "react-hook-form";
import { useMerchant, usePaymentSettings, canUpdatePaymentRequestTemplate } from "@auth";
import {
  getCustomerPreviewValues,
  invoicesRequestedTokenMap,
  formatAmount,
  htmlToPlainText,
} from "@utils";

import NamePage from "./components/NamePage";
import SaveAsPage from "./components/SaveAsPage";
import EditorPage from "./components/EditorPage";

const PAGES = {
  NAME: "name",
  EDITOR: "editor",
  SAVE_AS: "saveAs",
};

const getDefaultValues = (merchantName) => ({
  name: "",
  invoicesRequested: "Overdue",
  mandatory: false,
  attachPdfStatement: false,
  emailSubject: `Payment request from ${merchantName || "us"}`,
  emailTitle: "Payment request",
  emailMainText: "Hi {customer_name},\n\n",
  emailActionTitle: "{invoices_requested}",
  emailButtonText: "Review and pay",
  amountDisplay: "{amount_requested}",
  smsText: "Hi {customer_name},\n\n",
  allowCardPayment: false,
  paymentProfileId: null,
});

const CreateEditTemplate = ({
  isOpen,
  onClose,
  template = null,
  onComplete,
}) => {
  const isEditMode = !!template;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("email");
  const [page, setPage] = useState(PAGES.NAME);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");

  const { merchant, hasPermission } = useMerchant();
  const { paymentSettings } = usePaymentSettings();
  const canEdit = hasPermission(canUpdatePaymentRequestTemplate);
  const originalRef = useRef(null);

  const defaultValues = useMemo(
    () => getDefaultValues(merchant?.name),
    [merchant?.name],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields },
    reset,
    clearErrors,
    watch,
    getValues,
    setValue,
  } = useForm({ defaultValues });

  const watchedInvoicesRequested = watch("invoicesRequested");

  const previewTokens = useMemo(() => {
    const invoiceLabel =
      watchedInvoicesRequested === "Custom"
        ? "Custom amount"
        : (invoicesRequestedTokenMap[watchedInvoicesRequested] ??
          watchedInvoicesRequested);

    const amountMap = {
      Overdue: 1240.32,
      OverdueAndDueThisMonth: 1240.32 + 191.66,
      All: 1931.98,
      None: 1931.98,
      Custom: 123.45,
    };
    const amountValue = formatAmount(amountMap[watchedInvoicesRequested] ?? 0);

    return getCustomerPreviewValues().map((t) => {
      if (t.token === "invoices_requested")
        return { ...t, value: invoiceLabel };
      if (t.token === "amount_requested") return { ...t, value: amountValue };
      return t;
    });
  }, [watchedInvoicesRequested]);

  useEffect(() => {
    if (isOpen) {
      reset(
        isEditMode
          ? extractFormValues(template)
          : {
              ...defaultValues,
              allowCardPayment:
                paymentSettings?.paymentRequests?.allowCard ?? false,
            },
      );
      setPage(isEditMode ? PAGES.EDITOR : PAGES.NAME);

      if (isEditMode) {
        const hasEmail = template.messages?.some(
          (m) => m.communicationMethod === "Email",
        );
        const hasSms = template.messages?.some(
          (m) => m.communicationMethod === "Sms",
        );
        setEmailEnabled(hasEmail ?? false);
        setSmsEnabled(hasSms ?? false);
        setActiveTab(hasEmail ? "email" : hasSms ? "sms" : "email");
        originalRef.current = {
          emailEnabled: hasEmail ?? false,
          smsEnabled: hasSms ?? false,
        };
      } else {
        setEmailEnabled(true);
        setSmsEnabled(true);
        setActiveTab("email");
        originalRef.current = null;
      }
    }
  }, [isOpen, template, isEditMode, reset, paymentSettings, defaultValues]);

  useEffect(() => {
    if (!isOpen) {
      setPage(PAGES.NAME);
    }
  }, [isOpen]);

  const goToName = useCallback(() => setPage(PAGES.NAME), []);
  const goToEditor = useCallback(() => setPage(PAGES.EDITOR), []);

  const orig = originalRef.current;
  const channelEdited =
    isEditMode &&
    orig != null &&
    (emailEnabled !== orig.emailEnabled || smsEnabled !== orig.smsEnabled);
  const contentDirty = Object.keys(dirtyFields).some((k) => k !== "name");
  const isEdited = isEditMode && (contentDirty || channelEdited);

  const handleResetEdits = useCallback(() => {
    if (!template) return;
    reset(extractFormValues(template));
    if (orig) {
      setEmailEnabled(orig.emailEnabled);
      setSmsEnabled(orig.smsEnabled);
      setActiveTab(orig.emailEnabled ? "email" : "sms");
    }
    setPreviewMode(false);
  }, [template, orig, reset]);

  const handleGoToSaveAs = useCallback(() => {
    setSaveAsName(getValues("name") ? `${getValues("name")} (copy)` : "");
    setPage(PAGES.SAVE_AS);
  }, [getValues]);

  const buildMessages = (values) => {
    const messages = [];

    if (emailEnabled) {
      messages.push({
        communicationMethod: "Email",
        content: {
          subject: values.emailSubject,
          title: values.emailTitle,
          mainText: values.emailMainText,
          actionArea: {
            title: values.emailActionTitle,
            description: "",
            amount: values.amountDisplay,
            buttonText: values.emailButtonText,
          },
        },
      });
    }

    if (smsEnabled) {
      messages.push({
        communicationMethod: "Sms",
        content: {
          text: values.smsText,
        },
      });
    }

    return messages;
  };

  const handleSaveAs = async () => {
    if (!saveAsName.trim()) return;
    setLoading(true);
    try {
      const values = getValues();
      const messages = buildMessages(values);
      const paymentRequirement = values.mandatory ? "Mandatory" : "Flexible";
      const invoicesRequested =
        values.invoicesRequested === "Custom" ? null : values.invoicesRequested;

      await createPaymentRequestTemplate(
        saveAsName.trim(),
        invoicesRequested,
        paymentRequirement,
        values.attachPdfStatement,
        messages,
        values.allowCardPayment,
        values.paymentProfileId ?? null,
      );

      toast({ description: `Created "${saveAsName.trim()}" Template` });
      onComplete();
      handleClose();
    } catch (err) {
      console.error("Failed to save template as copy:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChannel = useCallback(
    (channel) => {
      if (channel === "email") {
        const next = !emailEnabled;
        setEmailEnabled(next);
        if (next && !getValues("emailMainText")) {
          setValue(
            "emailMainText",
            getValues("smsText") || defaultValues.emailMainText,
          );
        }
        if (!next && activeTab === "email") {
          setActiveTab(smsEnabled ? "sms" : "email");
        }
      } else {
        const next = !smsEnabled;
        setSmsEnabled(next);
        if (next && !getValues("smsText")) {
          setValue(
            "smsText",
            htmlToPlainText(getValues("emailMainText")) || defaultValues.smsText,
          );
        }
        if (!next && activeTab === "sms") {
          setActiveTab(emailEnabled ? "email" : "sms");
        }
      }
    },
    [emailEnabled, smsEnabled, activeTab, getValues, setValue],
  );

  const saveTemplate = async (values) => {
    setLoading(true);
    const messages = buildMessages(values);
    const paymentRequirement = values.mandatory ? "Mandatory" : "Flexible";
    const invoicesRequested =
      values.invoicesRequested === "Custom" ? null : values.invoicesRequested;

    if (isEditMode) {
      await updatePaymentRequestTemplate(
        template.id,
        values.name,
        invoicesRequested,
        paymentRequirement,
        values.attachPdfStatement,
        messages,
        values.allowCardPayment,
        values.paymentProfileId ?? null,
      );
      toast({ description: `Updated "${values.name}" Template` });
    } else {
      await createPaymentRequestTemplate(
        values.name,
        invoicesRequested,
        paymentRequirement,
        values.attachPdfStatement,
        messages,
        values.allowCardPayment,
        values.paymentProfileId ?? null,
      );
      toast({ description: `Created "${values.name}" Template` });
    }

    setLoading(false);
    onComplete();
    handleClose();
  };

  const handleClose = () => {
    reset(defaultValues);
    clearErrors();
    onClose();
  };

  const handleNameSubmitCreate = () => {
    handleSubmit(() => goToEditor())();
  };

  const handleNameSubmitEdit = () => {
    handleSubmit(saveTemplate)();
  };

  const getModalTitle = () => {
    const name = getValues("name");
    switch (page) {
      case PAGES.NAME:
        return isEditMode ? "Change template name" : "Template name";
      case PAGES.SAVE_AS:
        return "Save as new template";
      case PAGES.EDITOR:
      default:
        return name || (isEditMode ? "Edit template" : "Create template");
    }
  };

  const renderPage = () => {
    switch (page) {
      case PAGES.NAME:
        return (
          <NamePage
            isEditMode={isEditMode}
            control={control}
            errors={errors}
            loading={loading}
            onBack={isEditMode ? handleClose : handleClose}
            onSubmit={
              isEditMode ? handleNameSubmitEdit : handleNameSubmitCreate
            }
          />
        );

      case PAGES.SAVE_AS:
        return (
          <SaveAsPage
            saveAsName={saveAsName}
            onNameChange={setSaveAsName}
            loading={loading}
            onBack={goToEditor}
            onSubmit={handleSaveAs}
          />
        );

      case PAGES.EDITOR:
        return (
          <EditorPage
            control={control}
            errors={errors}
            watch={watch}
            getValues={getValues}
            setValue={setValue}
            handleSubmit={handleSubmit}
            merchant={merchant}
            isEditMode={isEditMode}
            loading={loading}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            emailEnabled={emailEnabled}
            smsEnabled={smsEnabled}
            previewMode={previewMode}
            setPreviewMode={setPreviewMode}
            isEdited={isEdited}
            onToggleChannel={handleToggleChannel}
            onBack={isEditMode ? handleClose : goToName}
            onResetEdits={handleResetEdits}
            onSaveAs={handleGoToSaveAs}
            onSave={saveTemplate}
            previewTokens={previewTokens}
            canEdit={canEdit}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={getModalTitle()}
      size={
        page === PAGES.EDITOR
          ? "lg"
          : "sm"
      }
      fullHeight={page === PAGES.EDITOR}
      maxHeight="780px"
      preventBackdropClose
      bodyClassName={
        page === PAGES.EDITOR
          ? "overflow-hidden flex flex-col bg-white"
          : undefined
      }
    >
      {renderPage()}
    </Modal>
  );
};

export default CreateEditTemplate;
